// SelectorBeta data layer — real API calls behind the shapes the Beta selector
// components consume. Mirrors the design handoff's model.js contract 1:1, with
// the prototype's simulated availability replaced by the live endpoints:
//   GET /info/industries?type=if|imr&industry=<code|0>   (lazy tree, one call per branch)
//   GET /info/industries?type=if|imr&search=<q>          (keyword / code search)
//   GET /info/areas?type=if&industry=<code|0>            (flat state+metro rows)
//   GET /info/areas?type=imr&industry=<code>             ({states:[...]} — never call with industry=0, it times out)
//   GET /info/sales_classes?type=if&industry=<code>      (ONLY offered bands)
import axios from 'axios';

const BASE = process.env.VUE_APP_API_BASE_URL;

/* ---------------- sales class catalog (full 15 bands; live API returns the offered subset) ---------------- */
export const SALES = [
  { id: '1',      label: 'Industrywide', group: null },
  { id: 'small',  label: 'Small < $5M', group: null },
  { id: '2',      label: '$1–$499,999', group: 'small' },
  { id: '4',      label: '$500,000–$999,999', group: 'small' },
  { id: '5',      label: '$1m–$2.49m', group: 'small' },
  { id: '6',      label: '$2.5m–$4.99m', group: 'small' },
  { id: 'medium', label: 'Medium · $5m–$24.99m', group: null },
  { id: '7',      label: '$5m–$9.99m', group: 'medium' },
  { id: '8',      label: '$10m–$24.99m', group: 'medium' },
  { id: 'large',  label: 'Large > $25m', group: null },
  { id: '9',      label: '$25m–$49.99m', group: 'large' },
  { id: '10',     label: '$50m–$99.99m', group: 'large' },
  { id: '11',     label: '$100m–$249.99m', group: 'large' },
  { id: '12',     label: '$250m–$499.99m', group: 'large' },
  { id: '17',     label: '> $500m', group: null },
];
export const SALES_BY_ID = SALES.reduce((m, s) => { m[s.id] = s; return m; }, {});

function normNode(row) {
  return {
    code: String(row.bznaics),
    name: row.name,
    state: row.selector_state || 'normal',
    msg: row.selector_message || '',
    // offered-sales-class count (backend joins if_selector_class_counts;
    // null = unknown/not indexed — show no chip rather than claiming zero)
    nClasses: typeof row.n_classes === 'number' ? row.n_classes : null,
  };
}

function ok(res) {
  if (res && res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  return res.data.data;
}

/* ---------------- industries: lazy tree + search ---------------- */
const childCache = {};
export async function fetchChildren(type, code) {
  const key = type + '|' + (code || '0');
  if (childCache[key]) return childCache[key];
  const res = await axios.get(BASE + '/info/industries', {
    params: { type, industry: code || '0', area: 0, sales_class: '0' },
  });
  const rows = (ok(res) || []).map(normNode);
  childCache[key] = rows;
  return rows;
}

let searchCancel = null;
export async function searchIndustries(type, q) {
  if (searchCancel) searchCancel.cancel('superseded');
  searchCancel = axios.CancelToken.source();
  const res = await axios.get(BASE + '/info/industries', {
    params: { type, search: q, industry: '0', area: 0, sales_class: '0' },
    cancelToken: searchCancel.token,
  });
  return (ok(res) || []).map(normNode);
}

/* ---------------- sales classes (industry-scoped; this IS the availability signal) ---------------- */
const salesCache = {};
export async function fetchOfferedSalesClasses(code) {
  if (salesCache[code]) return salesCache[code];
  const res = await axios.get(BASE + '/info/sales_classes', {
    params: { type: 'if', industry: code },
  });
  const ids = (ok(res) || []).map((r) => String(r.id));
  salesCache[code] = ids;
  return ids;
}

/* ---------------- pre-generation data availability ----------------
 * /info/sales_classes above returns only the OFFERED bands, not which bands
 * actually have data for a given industry+area. This probes the real thing:
 * IF_available (or SPIF_available for sole proprietorships) returns
 *   0            = no IF data for this exact NAICS + area + sales class
 *   -1           = not offered at this granularity
 *   {firms,sales}= available
 * Used to block generation before the user pays when a segment is empty
 * (e.g. a decimal NAICS whose sc2 was zeroed in the 3_26 rebuild). */
export async function fetchIfAvailability({ industryCode, areaCode, salesClassId, soleProp }) {
  const path = soleProp ? '/info/SPIF_available' : '/info/IF_available';
  const res = await axios.get(BASE + path, {
    params: { industry: industryCode, area: areaCode, sales_class: salesClassId },
  });
  return res && res.data ? res.data.data : null;
}

// Legacy (archive-year) add-on availability — IFP + All-US only, mirrors the
// classic selector's "Add legacy year(s)" checkbox. Returns
// {available, yearsRange, years, price} or null.
export async function fetchIfLegacyAvailability({ industryCode, salesClassId }) {
  const res = await axios.get(BASE + '/info/IF_legacy_available', {
    params: { industry: industryCode, sales_class: salesClassId },
  });
  return res && res.data ? res.data : null;
}

// IMP pre-purchase availability. Unlike IFP (single-area-code selections only --
// see probeAvailability's "radius/zips: no single-area check"), IMP has to cover
// zip, county and radius picks too: that is the gap a real customer purchase fell
// through (profile 18810991, decimal NAICS 812320.070100, zero real businesses in
// the one ZIP they picked). GET /info/IMR_selection_available runs ONE indexed
// count against the same establishment table + geography resolution the IMP
// report itself reads (decimal NAICS handled the way the report does), so the
// answer cannot disagree with what the report would contain. (The older
// /info/IMR_available call this replaces fed the raw decimal code into the table
// name, so a decimal industry raised a server error and the guard failed open.)
// Returns {available: boolean, estabs: number, checked: boolean} or null when the
// selection kind isn't probe-able -- callers fail open on null/errors.
export function imrSelectionParams(area) {
  if (!area) return null;
  if (area.kind === 'us') return { kind: 'us' };
  if (area.kind === 'state' && area.stateId != null) return { kind: 'state', state_id: area.stateId };
  if (area.kind === 'metro' && area.metroId != null) return { kind: 'metro', metro_id: area.metroId };
  if (area.kind === 'county' && area.countyId != null) return { kind: 'county', county_id: area.countyId };
  if (area.kind === 'zip' && area.zips && area.zips.length) return { kind: 'zip', zips: area.zips.join(',') };
  if (area.kind === 'radius' && area.radius) {
    return {
      kind: 'radius',
      radius_address: area.radius.address,
      radius_zip: area.radius.zip || '',
      radius_miles: String(area.radius.miles),
    };
  }
  return null;
}

export async function fetchImrSelectionAvailability({ industryCode, area }) {
  const areaParams = imrSelectionParams(area);
  if (!areaParams) return null;
  const res = await axios.get(BASE + '/info/IMR_selection_available', {
    params: { industry: industryCode, ...areaParams },
  });
  const data = res && res.data ? res.data : null;
  if (!data || typeof data.available !== 'boolean') return null;
  return { available: data.available, estabs: Number(data.estabs) || 0, checked: data.checked !== false };
}

/* ---------------- market areas ---------------- */
// Full IF geography (states + their metros), industry-independent. One call, cached.
let ifUniverse = null;
export async function fetchIfAreaUniverse() {
  if (ifUniverse) return ifUniverse;
  const res = await axios.get(BASE + '/info/areas', { params: { type: 'if', industry: '0' } });
  const rows = ok(res) || [];
  const byState = {};
  rows.forEach((r) => {
    if (!byState[r.state_id]) byState[r.state_id] = { id: r.state_id, name: null, metros: [] };
    if (r.metro_id === 0) byState[r.state_id].name = r.area;
    else byState[r.state_id].metros.push({ id: r.metro_id, name: r.area });
  });
  const states = Object.values(byState)
    .filter((s) => s.name)
    .sort((a, b) => a.name.localeCompare(b.name));
  states.forEach((s) => s.metros.sort((a, b) => a.name.localeCompare(b.name)));
  ifUniverse = { states };
  return ifUniverse;
}

// Which states/metros carry this industry (IFP): rows present = offered.
const ifAvailCache = {};
export async function fetchIfAreaAvailability(code) {
  if (ifAvailCache[code]) return ifAvailCache[code];
  const res = await axios.get(BASE + '/info/areas', { params: { type: 'if', industry: code } });
  const rows = ok(res) || [];
  const stateSet = {};
  const metroSet = {};
  rows.forEach((r) => {
    if (r.metro_id === 0) stateSet[r.state_id] = true;
    else metroSet[r.metro_id] = true;
  });
  const out = { stateSet, metroSet };
  ifAvailCache[code] = out;
  return out;
}

// IMP: states with operation counts for this industry. Response shape differs
// from type=if ({states:[{state_id, state, estabs}]}). Only ever call with a
// real industry code — the unfiltered call is known to time out server-side.
const imrStatesCache = {};
export async function fetchImrStates(code) {
  if (imrStatesCache[code]) return imrStatesCache[code];
  const res = await axios.get(BASE + '/info/areas', {
    params: { type: 'imr', industry: code },
    timeout: 45000,
  });
  const data = ok(res) || {};
  const states = (data.states || []).map((s) => ({ id: s.state_id, name: s.state, estabs: s.estabs }));
  imrStatesCache[code] = states;
  return states;
}

/* ---------------- profile creation + access (same wiring as the classic flow) ---------------- */
function session() {
  return localStorage.getItem('vue-authenticate.vueauth_access_token');
}

// POST /user/search_profiles/if — returns the profile id the access flow needs.
export async function createIfSearchProfile({ industryCode, areaCode, salesClassId, years, soleProp }) {
  const res = await axios.post(BASE + '/user/search_profiles/if', {}, {
    params: {
      session_id: session(),
      profile_id: '0',
      industry: industryCode,
      sales_class: salesClassId,
      area: areaCode,
      years,
      type: soleProp ? 'spif' : 'if',
    },
  });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  return res.data.id;
}

// POST /user/search_profiles/imr
export async function createImrSearchProfile({ industryCode, areaCode, radius, zips }) {
  const params = {
    session_id: session(),
    profile_id: '0',
    industry: industryCode,
    radius: '0',
  };
  if (zips && zips.length) {
    params.zips = zips.join(',');
    params.area = '0';
  } else if (radius) {
    params.mapCenter = radius.address;
    params.mapCenterZip = radius.zip || '';
    params.radius = String(radius.miles);
    params.area = '';
  } else {
    params.area = areaCode;
  }
  const res = await axios.post(BASE + '/user/search_profiles/imr', {}, { params });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  return res.data.id;
}

// Same warm-up call classic access() makes before cart/accessProduct.
export async function queueLoad(profileId, userId) {
  localStorage.setItem('queueUserID', userId);
  await axios.get(BASE + '/queue/load/' + profileId, { params: { session_id: session() } });
}

/* ---------------- area code helper ---------------- */
// The API's `area` param: 9999 = All US, state rows use state_id, metros use metro_id.
export function areaCodeOf(area) {
  if (!area) return null;
  if (area.kind === 'us') return 9999;
  if (area.kind === 'state') return area.stateId;
  if (area.kind === 'metro') return area.metroId;
  if (area.kind === 'county') return area.countyId;
  return null; // radius/zips have no single area code
}

/* ==================== v4/v5 additions: Company, Prospect, Reports ==================== */

// ---- Company Profile (classic wiring: company-service.js) ----
// state must be a 2-letter code (matches the DA `state` column). Omit it
// entirely for "all states" — the backend applies where('state', ...) literally,
// so sending 'auto' or a full name matches zero rows.
export async function searchCompanies(term, state, page = 1) {
  const body = { session_id: session(), term, page, itemsPerPage: 15 };
  if (state && state !== 'auto') body.state = state;
  const res = await axios.post(BASE + '/user/search_lists/companyProfiles', body);
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  return res.data.list || [];
}

export async function createCompanyProfile(locnum, zip) {
  const res = await axios.post(BASE + '/user/create/companyProfile', {
    session_id: session(),
    locnum,
    zip,
  });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  return res.data.id;
}

// ---- Prospect List (classic wiring: ProspectSearchLayout.filter()) ----
// filters: {industry, area, zips, sales_from, sales_to, value_from, value_to,
//           employee_count, years_in_business, contact_phone, franchise,
//           web_presence, social_presence}
let prospectCancel = null;
export async function prospectCount(filters) {
  if (prospectCancel) prospectCancel.cancel('superseded');
  prospectCancel = axios.CancelToken.source();
  const res = await axios.post(BASE + '/user/search_lists/company', {
    type: 'company',
    session_id: session(),
    profile_id: '0',
    countOnly: 1,
    ...filters,
  }, { cancelToken: prospectCancel.token });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  return { count: res.data.count, id: res.data.id, price: res.data.price, credits: res.data.credits };
}

// "Max available" baseline count for the current industry + area with NO refine filters
// (7.18 #6b: show the max available, then the filtered number). It needs its own cancel
// token — sharing prospectCancel would make the filtered count cancel this one (or vice
// versa) since both fire on the same change.
let prospectMaxCancel = null;
export async function prospectMaxCount(baseFilters) {
  if (prospectMaxCancel) prospectMaxCancel.cancel('superseded');
  prospectMaxCancel = axios.CancelToken.source();
  const res = await axios.post(BASE + '/user/search_lists/company', {
    type: 'company',
    session_id: session(),
    profile_id: '0',
    countOnly: 1,
    ...baseFilters,
  }, { cancelToken: prospectMaxCancel.token });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  return { count: res.data.count };
}

export async function prospectMaterialize(filters) {
  const res = await axios.post(BASE + '/user/search_lists/company', {
    type: 'company',
    session_id: session(),
    profile_id: '0',
    ...filters,
  });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  return { count: res.data.count, id: res.data.id };
}

// ---- Reports ----
export async function fetchMyDataSets() {
  const res = await axios.post(BASE + '/user/my_data/all', { session_id: session() });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  // `my_data` has NO `name` column — the label lives in `description`, and the
  // secondary note in `dataSource` (see classic MyData.vue). Normalize to the
  // shape the picker/rail read (`name`, crumb, `modified`).
  return (res.data.my_data || []).map((r) => ({
    ...r,
    name: r.description,
    description: r.dataSource || '',
    modified: r.modified || r.dateUpdated || null,
  }));
}

// Classic parity (MyData.vue): the label is stored in `description`, the source
// note in `data_source`, and new sets are always `advanced`. `name` is not a
// real column, so sending it does nothing — and omitting `type` leaves the set
// with no year table, which returns zero years on attach.
export async function createMyDataSet(label, source) {
  const res = await axios.post(BASE + '/user/my_data/add', {
    session_id: session(),
    type: 'advanced',
    description: label,
    data_source: source || '',
  });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  return res.data;
}

// Financial/Market reports use the search_reports twins of the profile endpoints.
export async function createSearchReport(type, { industryCode, areaCode, salesClassId, years, radius, zips }) {
  const params = {
    session_id: session(),
    profile_id: '0',
    industry: industryCode,
    type,
  };
  if (type === 'if' || type === 'spif') {
    params.sales_class = salesClassId;
    params.area = areaCode;
    params.years = years;
  } else {
    params.radius = '0';
    if (zips && zips.length) {
      params.zips = zips.join(',');
      params.area = '0';
    } else if (radius) {
      params.mapCenter = radius.address;
      params.mapCenterZip = radius.zip || '';
      params.radius = String(radius.miles);
      params.area = '';
    } else {
      params.area = areaCode;
    }
  }
  const res = await axios.post(BASE + '/user/search_reports/' + (type === 'imr' ? 'imr' : 'if'), {}, { params });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  return res.data.id;
}

// Valuation/Company reports have dedicated create endpoints (classic wiring).
export async function createStandaloneReport(kind, { industryCode, areaCode, salesClassId }) {
  const url = kind === 'valuation' ? '/user/create/valuationReport' : '/user/create/companyReport';
  const res = await axios.post(BASE + url, {}, {
    params: {
      session_id: session(),
      product_id: '0',
      industry: industryCode,
      sales_class: salesClassId,
      area: areaCode,
      map_center: '0',
      map_center_zip: '0',
      radius: '0',
      zips: '0',
      type: kind === 'valuation' ? 'valuation' : 'company',
    },
  });
  if (res.data && res.data.error) throw new Error('API error: ' + res.data.errorCode);
  return res.data.id;
}

/* ==================== IMP per-state metros ==================== */
// GET /info/areas?type=imr&state_id=N&industry=code → {metros:[{metro_id, CBSA_Title}]}
// The backend serves this from the pre-dumped LMR table (indexed) and returns
// ONLY metros with data — no-data metros are hidden entirely, so a single
// scoped call is all that's needed.
const imrAreaCache = {};
export async function fetchImrStateAreas(industryCode, stateId) {
  const key = industryCode + '|' + stateId;
  if (imrAreaCache[key]) return imrAreaCache[key];
  const res = await axios.get(BASE + '/info/areas', {
    params: { type: 'imr', state_id: stateId, industry: industryCode },
    timeout: 45000,
  });
  const data = ok(res) || {};
  const out = {
    metros: (data.metros || []).map((m) => ({ id: m.metro_id, name: m.CBSA_Title, available: true })),
    counties: (data.counties || []).map((c) => ({ id: c.county_id, name: c.county })),
    zips: (data.zips || []).map((z) => String(z.zip)),
  };
  imrAreaCache[key] = out;
  return out;
}

/* ==================== access-path watchdog ==================== */
// The dev API can stall for minutes; without this a slow call leaves the CTA
// on "Processing…" forever with no feedback.
export function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout: ' + label)), ms)),
  ]);
}
